import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CiStar } from "react-icons/ci";
import { FaStar } from "react-icons/fa";
import { LuPlus, LuTrash2, LuX } from "react-icons/lu";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";

import { Button } from "../../../../../components/newUI/button";
import { Divider } from "../../../../../components/newUI/divider";
import { Input } from "../../../../../components/newUI/input";
import { Select, SelectOption } from "../../../../../components/newUI/select";
import { FieldLabel, SectionTitle } from "../../../../../components/newUI/text";
import {
  Address,
  UpdateOrganizationModel,
} from "../../../../../factories/contacts/models/update-organization.model";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import {
  useEditOrgContactMutation,
  useGetContactTypesQuery,
  useGetOrganizationTypesQuery,
  useGetOrgContactQuery,
  useGetPhoneTypesQuery,
} from "../../../../../slices/ContactsSlice";
import {
  IOrganizationContact,
  reportingDateGst,
  reportingDatePst,
  SelectOptionType,
} from "../../../../../types/types";
import { isValidEmail } from "../../../../../utils/email";
import { CustomFieldsEditSection } from "../../../../customFields/CustomFieldsEditSection";
import { useCustomFieldValues } from "../../../../customFields/useCustomFieldValues";
import { useDuplicateEmailWarning } from "../../../duplicateEmail/useDuplicateEmailWarning";
import { formatDateOnly } from "../../../utils/dateOnly";
import {
  businessIdMaxLength,
  businessIdProblem,
  formatBusinessId,
} from "../../../../region/region";
import { useFirmRegion } from "../../../../region/useFirmRegion";
import {
  PERSON_NAME_PATTERN,
  PERSON_NAME_RULE,
} from "../../../utils/nameRules";
import {
  formatPhoneInput,
  phoneNumbersProblem,
} from "../../../utils/phoneNumber";
import {
  ADDRESS_TYPES,
  DEFAULT_ADDRESS_TYPE,
  DEFAULT_PHONE_TYPE,
  getDaysOptions,
  MonthsEnum,
  monthsOptions,
  PRIMARY_EMAIL_TOOLTIP_ID,
} from "./options";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type EmailDraft = {
  emailAddress: string;
  id: string;
  isPrimaryEmail: boolean;
};

type PhoneDraft = {
  id: string;
  number: string;
  type: SelectOption;
};

type FormSelectOption = Pick<
  SelectOption,
  "caption" | "isBold" | "label" | "totalCount" | "value"
>;

type FormData = {
  companyName: string;
  dba: string;
  businessNumber: string;
  yearEndMonth: FormSelectOption;
  yearEndDay: FormSelectOption;
  salesTaxId: FormSelectOption;
  organizationTypeId: FormSelectOption;
  contactType: FormSelectOption;
  additionalInfo: string;
  incorporationDate: string;
  gstHstNumber: string;
  pstNumber: string;
  gstHstReportingPeroid: FormSelectOption;
  pstReportingPeroid: FormSelectOption;
  contactPersonFirstName: string;
  contactPersonLastName: string;
};

const iconButtonClassName =
  "h-9 w-9 shrink-0 rounded-[10px] border-[#E7E9F0] text-[#C3C8D4] hover:bg-[#F7F8FA]";

type SelectChangeValue = SelectOption | SelectOption[] | null;

const getSingleSelectOption = <TOption extends SelectOption>(
  value: SelectChangeValue,
): TOption | null => {
  if (Array.isArray(value)) {
    return (value[0] as TOption | undefined) ?? null;
  }

  return (value as TOption | null) ?? null;
};

const getSelectValue = <TOption extends SelectOption>(
  value?: TOption | null,
) => {
  return value?.value ? value : null;
};

const mapSelectOption = (option: SelectOptionType): SelectOption => ({
  label: option.label,
  value: option.value,
  caption: option.caption,
  isBold: option.isBold,
  totalCount: option.totalCount,
});

const getErrorMessage = (error: unknown) => {
  const data = (error as FetchBaseQueryError | undefined)?.data;

  if (typeof data !== "string") {
    return null;
  }

  return data.split(":")[1]?.trim() || data;
};

const formatPhoneNumber = (value: string) => formatPhoneInput(value);

const formatDateInput = (value?: Date | string | null) => {
  return formatDateOnly(value, "yyyy/MM/dd");
};

const isLeapYear = (year: number) => {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
};

const getDaysInMonth = (year: number, month: number) => {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }

  if ([4, 6, 9, 11].includes(month)) {
    return 30;
  }

  return 31;
};

const formatDateMask = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  const year = digits.slice(0, 4);
  const month = digits.slice(4, 6);
  const day = digits.slice(6, 8);

  if (digits.length <= 4) {
    return year;
  }

  if (digits.length <= 6) {
    return `${year}/${month}`;
  }

  return `${year}/${month}/${day}`;
};

const validateDateInput = (value: string) => {
  if (!value.trim()) {
    return true;
  }

  const match = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(value);

  if (!match) {
    return "Use YYYY/MM/DD format";
  }

  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);

  if (year < 1000 || month < 1 || month > 12) {
    return "Enter a valid date";
  }

  if (day < 1 || day > getDaysInMonth(year, month)) {
    return "Enter a valid date";
  }

  // QA batch 15 (PROF-017): an incorporation date can't be in the future.
  const today = new Date();
  const todayKey =
    today.getFullYear() * 10000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();
  if (year * 10000 + month * 100 + day > todayKey) {
    return "The date can't be in the future";
  }

  return true;
};

const parseDateInput = (value: string) => {
  if (!value.trim() || validateDateInput(value) !== true) {
    return null;
  }

  const [year, month, day] = value.split("/").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const buildEmailDrafts = (
  organization?: IOrganizationContact,
): EmailDraft[] => {
  if (organization?.emails?.length) {
    return organization.emails.map((email) => ({
      id: email.id || uuidv4(),
      emailAddress: email.emailAddress || "",
      isPrimaryEmail: Boolean(email.isPrimaryEmail),
    }));
  }

  return [
    {
      id: uuidv4(),
      emailAddress: "",
      isPrimaryEmail: false,
    },
  ];
};

const buildPhoneDrafts = (
  phones?: IOrganizationContact["phoneNumbers"],
): PhoneDraft[] => {
  if (phones?.length) {
    return phones.map((phone) => ({
      id: phone.id || uuidv4(),
      number: phone.number || "",
      type: {
        value: phone.phoneNumberTypeEnumId || DEFAULT_PHONE_TYPE.value,
        label:
          phone.phoneNumberTypeEnum?.phoneNumberType ||
          DEFAULT_PHONE_TYPE.label,
      },
    }));
  }

  return [
    {
      id: uuidv4(),
      number: "",
      type: DEFAULT_PHONE_TYPE,
    },
  ];
};

const buildAddressDrafts = (organization?: IOrganizationContact): Address[] => {
  if (organization?.addresses?.length) {
    return organization.addresses.map((address) => ({
      id: address.id || uuidv4(),
      line1: address.line1 || "",
      city: address.city || "",
      region: address.region || "",
      postalCode: address.postalCode || "",
      addressTypeEnumId: address.addressTypeEnumId || DEFAULT_ADDRESS_TYPE,
    }));
  }

  return [
    {
      id: uuidv4(),
      line1: "",
      city: "",
      region: "",
      postalCode: "",
      addressTypeEnumId: DEFAULT_ADDRESS_TYPE,
    },
  ];
};

export const EditOrganizationModal: FC<Props> = ({ isOpen, onClose }) => {
  const region = useFirmRegion();
  const { organizationId } = useParams();
  const { data: organization, isLoading: isLoadingOrganization } =
    useGetOrgContactQuery({
      id: organizationId || "",
      orgId: getLinkedFirm()?.orgId || "",
    });
  const { data: contactTypes = [], isLoading: isLoadingContactTypes } =
    useGetContactTypesQuery();
  const {
    data: organizationTypes = [],
    isLoading: isLoadingOrganizationTypes,
  } = useGetOrganizationTypesQuery();
  const { data: phoneTypes = [], isLoading: isLoadingPhoneTypes } =
    useGetPhoneTypesQuery();

  const [
    editOrganization,
    { isSuccess, isLoading: isLoadingEdit, error: errorEdit },
  ] = useEditOrgContactMutation();

  const [emailError, setEmailError] = useState("");
  const { confirmEmails, dialog: duplicateEmailDialog } =
    useDuplicateEmailWarning();
  const customFields = useCustomFieldValues(
    organization?.customerId || organization?.businessCustomers?.[0]?.id,
    "organization",
  );
  const [customFieldError, setCustomFieldError] = useState("");
  const [emailsData, setEmailsData] = useState<EmailDraft[]>(() =>
    buildEmailDrafts(organization),
  );
  const [phoneData, setPhoneData] = useState<PhoneDraft[]>(() =>
    buildPhoneDrafts(organization?.phoneNumbers),
  );
  // Phones need 7+ digits (Roadmap 1803); shown after the first Save.
  const [phoneChecked, setPhoneChecked] = useState(false);
  const phoneError = phoneChecked
    ? phoneNumbersProblem(phoneData.map((phone) => phone.number))
    : "";
  const [addressesData, setAddressesData] = useState<Address[]>(() =>
    buildAddressDrafts(organization),
  );

  const reportingPeriodGst: SelectOption[] = useMemo(
    () =>
      reportingDateGst.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [],
  );
  const reportingPeriodPst: SelectOption[] = useMemo(
    () =>
      reportingDatePst.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [],
  );
  const phoneTypeOptions = useMemo(
    () => phoneTypes.map(mapSelectOption),
    [phoneTypes],
  );
  const organizationTypeOptions = useMemo(
    () => organizationTypes.map(mapSelectOption),
    [organizationTypes],
  );
  const contactTypeOptions = useMemo(
    () => contactTypes.map(mapSelectOption),
    [contactTypes],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      companyName: organization?.companyName || "",
      dba: organization?.dba || "",
      businessNumber: organization?.businessNumber || "",
      yearEndMonth: {
        value: organization?.yearEndMonth || "",
        label: organization?.yearEndMonth || "",
      },
      yearEndDay: {
        value: organization?.yearEndDay?.toString() || "",
        label: organization?.yearEndDay?.toString() || "",
      },
      salesTaxId: {
        value: organization?.salesTaxId || "",
        label: organization?.salesTaxType?.salesTaxType || "",
      },
      organizationTypeId: {
        value: organization?.organizationTypeId || "",
        label: organization?.organizationType?.organizationType || "",
      },
      contactType: {
        value: organization?.contactTypeEnumId || "",
        label: organization?.contactType?.contactType || "",
      },
      additionalInfo: organization?.additionalInfo || "",
      incorporationDate: formatDateInput(organization?.incorporationDate),
      gstHstNumber: organization?.gstHstNumber || "",
      pstNumber: organization?.pstNumber || "",
      gstHstReportingPeroid: {
        value: organization?.gstHstReportingPeroid || "",
        label: organization?.gstHstReportingPeroid || "",
      },
      pstReportingPeroid: {
        value: organization?.pstReportingPeroid || "",
        label: organization?.pstReportingPeroid || "",
      },
      contactPersonFirstName: organization?.contactPersons?.firstName || "",
      contactPersonLastName: organization?.contactPersons?.lastName || "",
    },
  });

  const companyName = watch("companyName");
  const selectedContactType = watch("contactType");
  const selectedContactTypeValue = selectedContactType?.value;

  const contactTypeButtons = useMemo(() => {
    const options =
      contactTypeOptions.length || !selectedContactType?.value
        ? contactTypeOptions
        : [selectedContactType];

    return options.map((contactType) => ({
      ...contactType,
      isSelected: contactType.value === selectedContactTypeValue,
    }));
  }, [contactTypeOptions, selectedContactType, selectedContactTypeValue]);

  const submitError = getErrorMessage(errorEdit);
  const isSubmitting = isLoadingEdit;

  useEffect(() => {
    setValue("dba", companyName || "", { shouldDirty: true });
  }, [companyName, setValue]);

  useEffect(() => {
    if (!organization) {
      return;
    }

    reset({
      companyName: organization.companyName || "",
      dba: organization.dba || "",
      businessNumber: organization.businessNumber || "",
      yearEndMonth: {
        value: organization.yearEndMonth || "",
        label: organization.yearEndMonth || "",
      },
      yearEndDay: {
        value: organization.yearEndDay?.toString() || "",
        label: organization.yearEndDay?.toString() || "",
      },
      salesTaxId: {
        value: organization.salesTaxId || "",
        label: organization.salesTaxType?.salesTaxType || "",
      },
      organizationTypeId: {
        value: organization.organizationTypeId || "",
        label: organization.organizationType?.organizationType || "",
      },
      contactType: {
        value: organization.contactTypeEnumId || "",
        label: organization.contactType?.contactType || "",
      },
      additionalInfo: organization.additionalInfo || "",
      incorporationDate: formatDateInput(organization.incorporationDate),
      gstHstNumber: organization.gstHstNumber || "",
      pstNumber: organization.pstNumber || "",
      gstHstReportingPeroid: {
        value: organization.gstHstReportingPeroid || "",
        label: organization.gstHstReportingPeroid || "",
      },
      pstReportingPeroid: {
        value: organization.pstReportingPeroid || "",
        label: organization.pstReportingPeroid || "",
      },
      contactPersonFirstName: organization.contactPersons?.firstName || "",
      contactPersonLastName: organization.contactPersons?.lastName || "",
    });
    setEmailsData(buildEmailDrafts(organization));
    setPhoneData(buildPhoneDrafts(organization.phoneNumbers));
    setPhoneChecked(false);
    setAddressesData(buildAddressDrafts(organization));
  }, [organization, reset]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Organization updated successfully");
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (errorEdit) {
      toast.error(submitError || "Failed to update organization");
    }
  }, [errorEdit, submitError]);

  const handleAddEmail = () => {
    setEmailsData((currentEmails) => [
      ...currentEmails,
      {
        id: uuidv4(),
        emailAddress: "",
        isPrimaryEmail: false,
      },
    ]);
  };

  const handleDeleteEmail = (index: number) => {
    setEmailsData((currentEmails) => {
      if (currentEmails.length <= 1) {
        return currentEmails;
      }

      const removedEmail = currentEmails[index];
      const nextEmails = currentEmails.filter(
        (_, emailIndex) => emailIndex !== index,
      );

      if (!removedEmail?.isPrimaryEmail || nextEmails.length === 0) {
        return nextEmails;
      }

      return nextEmails.map((email, emailIndex) => ({
        ...email,
        isPrimaryEmail: emailIndex === 0,
      }));
    });
  };

  const handleChangePrimaryEmail = (emailId: string) => {
    const targetEmail = emailsData.find((email) => email.id === emailId);

    if (
      !targetEmail ||
      targetEmail.isPrimaryEmail ||
      !targetEmail.emailAddress.trim()
    ) {
      return;
    }

    setEmailsData((currentEmails) =>
      currentEmails.map((email) => ({
        ...email,
        isPrimaryEmail: email.id === emailId,
      })),
    );
  };

  const handleEmailChange = (index: number, value: string) => {
    const isEmpty = value.trim() === "";

    setEmailError(isEmpty || isValidEmail(value) ? "" : "Invalid email");

    setEmailsData((currentEmails) =>
      currentEmails.map((email, emailIndex) =>
        emailIndex === index ? { ...email, emailAddress: value } : email,
      ),
    );
  };

  const handleAddPhone = () => {
    setPhoneData((currentPhones) => [
      ...currentPhones,
      {
        id: uuidv4(),
        number: "",
        type: DEFAULT_PHONE_TYPE,
      },
    ]);
  };

  const handleDeletePhone = (index: number) => {
    setPhoneData((currentPhones) =>
      currentPhones.length > 1
        ? currentPhones.filter((_, phoneIndex) => phoneIndex !== index)
        : currentPhones,
    );
  };

  const handleAddAddress = () => {
    setAddressesData((currentAddresses) => [
      ...currentAddresses,
      {
        id: uuidv4(),
        line1: "",
        city: "",
        region: "",
        postalCode: "",
        addressTypeEnumId: DEFAULT_ADDRESS_TYPE,
      },
    ]);
  };

  const handleDeleteAddress = (index: number) => {
    setAddressesData((currentAddresses) =>
      currentAddresses.length > 1
        ? currentAddresses.filter((_, addressIndex) => addressIndex !== index)
        : currentAddresses,
    );
  };

  const onSubmit = async (data: FormData) => {
    if (isLoadingEdit || !organization) {
      return;
    }

    if (phoneNumbersProblem(phoneData.map((phone) => phone.number))) {
      setPhoneChecked(true);
      return;
    }

    // Roadmap 1784: warn (never block) when a NEW email of this organization
    // is already used by another client of the firm.
    const proceed = await confirmEmails(
      emailsData.map((email) => email.emailAddress),
      {
        before: (organization.emails ?? []).map((email) => email.emailAddress),
        exceptCustomerId:
          organization.customerId || organization.businessCustomers?.[0]?.id,
      },
    );
    if (!proceed) {
      return;
    }

    const customFieldProblem = await customFields.save();
    setCustomFieldError(customFieldProblem || "");
    if (customFieldProblem !== null) {
      return;
    }

    const body: UpdateOrganizationModel = {
      id: organization.id || "",
      companyName: data.companyName || "",
      dba: data.dba || "",
      businessNumber: data.businessNumber || "",
      yearEndMonth: data.yearEndMonth.value as MonthsEnum,
      yearEndDay: +data.yearEndDay.value || 0,
      salesTaxId: data.salesTaxId.value || null,
      organizationTypeId: data.organizationTypeId.value || null,
      phoneNumbers: phoneData
        .filter((phone) => phone.number !== "")
        .map((phone) => ({
          number: phone.number,
          phoneNumberTypeEnumId: phone.type.value,
        })),
      emails: emailsData.map((email) => ({
        emailAddress: email.emailAddress,
        isPrimaryEmail: email.isPrimaryEmail,
      })),
      contactTypeEnumId: data.contactType.value,
      addresses: addressesData.map((address) => ({
        id: address.id,
        line1: address.line1 || "",
        city: address.city || "",
        region: address.region || "",
        postalCode: address.postalCode || "",
        addressTypeEnumId: address.addressTypeEnumId,
      })),
      incorporationDate: parseDateInput(data.incorporationDate),
      additionalInfo: data.additionalInfo || "",
      socialIdentificationNumber: organization.socialIdentificationNumber,
      contactStatusEnumId: organization.contactStatusEnumId,
      gstHstNumber: data.gstHstNumber || "",
      pstNumber: data.pstNumber || "",
      gstHstReportingPeroid: data.gstHstReportingPeroid.label || "",
      pstReportingPeroid: data.pstReportingPeroid.label || "",
      // QA batch 17: no email / phone lists for the contact person (this
      // window doesn't edit them). Empty lists deleted the contact person's
      // saved emails and phones on every save; without them the server keeps them.
      contactPersons: {
        firstName: data.contactPersonFirstName || "",
        lastName: data.contactPersonLastName || "",
      },
    };

    editOrganization(body);
  };

  const renderPhoneRows = (
    phones: PhoneDraft[],
    onAdd: () => void,
    onDelete: (index: number) => void,
    onChange: React.Dispatch<React.SetStateAction<PhoneDraft[]>>,
    addLabel: string,
  ) => (
    <div className={"grid gap-2.5"}>
      {phones.map((phone, index) => (
        <div key={phone.id} className={"flex items-start gap-2"}>
          <div className={"flex h-10 w-6 shrink-0 items-center"}>
            {index === 0 ? (
              <Button
                type={"button"}
                variant={"primary"}
                size={"xs"}
                iconOnly
                leftIcon={<LuPlus size={16} strokeWidth={3} />}
                className={"shrink-0"}
                aria-label={addLabel}
                onClick={onAdd}
              />
            ) : (
              <span className={"h-6 w-6"} />
            )}
          </div>
          <div className={"flex min-w-0 flex-1 items-center gap-2"}>
            <div className={"w-[130px] shrink-0"}>
              <Select
                options={
                  phoneTypeOptions.length ? phoneTypeOptions : [phone.type]
                }
                value={phone.type}
                placeholder={"Select type"}
                loading={isLoadingPhoneTypes}
                size={"md"}
                dropMenuVariant={"portal"}
                styleConfig={{
                  fullWidth: true,
                  menuWidth: 180,
                  maxMenuHeight: 220,
                  controlClassName: "min-h-0",
                }}
                onChange={(value) => {
                  const newType = getSingleSelectOption(value);

                  if (!newType) {
                    return;
                  }

                  onChange((currentPhones) =>
                    currentPhones.map((currentPhone, phoneIndex) =>
                      phoneIndex === index
                        ? { ...currentPhone, type: newType }
                        : currentPhone,
                    ),
                  );
                }}
              />
            </div>
            <Input
              placeholder={"Phone number"}
              value={phone.number}
              onChange={(event) => {
                const formattedPhone = formatPhoneNumber(event.target.value);
                onChange((currentPhones) =>
                  currentPhones.map((currentPhone, phoneIndex) =>
                    phoneIndex === index
                      ? { ...currentPhone, number: formattedPhone }
                      : currentPhone,
                  ),
                );
              }}
            />
            <Button
              type={"button"}
              variant={"outline"}
              size={"md"}
              iconOnly
              leftIcon={<LuTrash2 size={17} />}
              className={classNames(
                iconButtonClassName,
                phones.length > 1
                  ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                  : "cursor-not-allowed text-[#C3C8D4]",
              )}
              disabled={phones.length <= 1}
              aria-label={"Delete phone number"}
              onClick={() => onDelete(index)}
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={classNames(
        "modal !z-[4]",
        isOpen ? "modal-open" : "modal-close",
      )}>
      <div
        className={
          "modal-box flex max-h-[calc(100vh-5em)] w-full max-w-[620px] flex-col overflow-hidden rounded-xl bg-white p-0"
        }>
        <div
          className={
            "grid grid-cols-[1fr_max-content] border-b border-[#EFF0F4] px-7 py-4"
          }>
          <p className={"text-lg font-bold text-[#1F2433]"}>Contact Info</p>
          <Button
            type={"button"}
            variant={"ghost"}
            size={"sm"}
            iconOnly
            leftIcon={<LuX size={18} />}
            className={"h-8 w-8 text-[#6B7185] hover:bg-[#F1F2F5]"}
            aria-label={"Close organization modal"}
            onClick={onClose}
          />
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={"flex min-h-0 flex-1 flex-col"}>
          <div className={"min-h-0 flex-1 overflow-y-auto px-7 py-6"}>
            {submitError && (
              <div
                className={
                  "mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-semibold text-error"
                }>
                {submitError}
              </div>
            )}

            <SectionTitle size={"sm"}>Company Details</SectionTitle>
            <div className={"mb-4"}>
              <FieldLabel label={"Company Name"} size={"sm"} />
              <Input
                placeholder={"Company name"}
                error={!!errors.companyName}
                {...register("companyName", {
                  required: "This field is required",
                  maxLength: {
                    value: 50,
                    message:
                      "Company name shouldn't be greater than 50 characters",
                  },
                  minLength: {
                    value: 2,
                    message: "Company name should be greater than 1 character",
                  },
                })}
              />
              {errors.companyName && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.companyName.message}
                </div>
              )}
            </div>
            <div className={"mb-4"}>
              <FieldLabel label={"Display Name"} size={"sm"} />
              <Input
                placeholder={"Display name"}
                error={!!errors.dba}
                {...register("dba", {
                  maxLength: {
                    value: 50,
                    message: "DBA shouldn't be greater than 50 characters",
                  },
                })}
              />
              {errors.dba && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.dba.message}
                </div>
              )}
            </div>

            <Divider />

            <SectionTitle size={"sm"}>Contact Person</SectionTitle>
            <div
              className={
                "mb-5 rounded-xl border border-[#E4E7EE] bg-[#F3F4F7] p-4"
              }>
              <div className={"mb-4 grid grid-cols-2 gap-4"}>
                <div>
                  <FieldLabel label={"First Name"} size={"sm"} />
                  <Input
                    placeholder={"First name"}
                    error={!!errors.contactPersonFirstName}
                    {...register("contactPersonFirstName", {
                      pattern: {
                        value: PERSON_NAME_PATTERN,
                        message: PERSON_NAME_RULE,
                      },
                      maxLength: {
                        value: 50,
                        message:
                          "First name shouldn't be greater than 50 characters",
                      },
                    })}
                  />
                  {errors.contactPersonFirstName && (
                    <div className={"pt-2 text-xs font-bold text-error"}>
                      {errors.contactPersonFirstName.message}
                    </div>
                  )}
                </div>
                <div>
                  <FieldLabel label={"Last Name"} size={"sm"} />
                  <Input
                    placeholder={"Last name"}
                    error={!!errors.contactPersonLastName}
                    {...register("contactPersonLastName", {
                      pattern: {
                        value: PERSON_NAME_PATTERN,
                        message: PERSON_NAME_RULE,
                      },
                      maxLength: {
                        value: 50,
                        message:
                          "Last name shouldn't be greater than 50 characters",
                      },
                    })}
                  />
                  {errors.contactPersonLastName && (
                    <div className={"pt-2 text-xs font-bold text-error"}>
                      {errors.contactPersonLastName.message}
                    </div>
                  )}
                </div>
              </div>

              <FieldLabel label={"Email"} size={"sm"} />
              <div className={"grid gap-2"}>
                {emailsData.map((email, index) => {
                  const isPrimaryActionDisabled =
                    !email.emailAddress.trim() || isLoadingEdit;
                  const primaryActionTooltip = email.isPrimaryEmail
                    ? "Already primary email"
                    : email.emailAddress.trim()
                    ? "Make primary email"
                    : "Enter email before making it primary";

                  return (
                    <div key={email.id} className={"flex items-start gap-2"}>
                      <div className={"flex h-10 w-6 shrink-0 items-center"}>
                        {index === 0 ? (
                          <Button
                            type={"button"}
                            variant={"primary"}
                            size={"xs"}
                            iconOnly
                            leftIcon={<LuPlus size={16} strokeWidth={3} />}
                            className={"shrink-0"}
                            aria-label={"Add email"}
                            onClick={handleAddEmail}
                          />
                        ) : (
                          <span className={"h-6 w-6"} />
                        )}
                      </div>
                      <div className={"flex min-w-0 flex-1 items-center gap-2"}>
                        <Input
                          placeholder={"Email"}
                          value={email.emailAddress}
                          onChange={(event) =>
                            handleEmailChange(index, event.target.value)
                          }
                        />
                        <Button
                          iconOnly
                          size={"md"}
                          variant={
                            email.isPrimaryEmail
                              ? "success-secondary"
                              : "outline"
                          }
                          leftIcon={
                            email.isPrimaryEmail ? (
                              <FaStar size={16} />
                            ) : (
                              <CiStar size={18} />
                            )
                          }
                          disabled={isPrimaryActionDisabled}
                          onClick={() => handleChangePrimaryEmail(email.id)}
                          type={"button"}
                          data-tooltip-id={PRIMARY_EMAIL_TOOLTIP_ID}
                          data-tooltip-content={primaryActionTooltip}
                          className={"shrink-0"}
                        />
                        <Button
                          type={"button"}
                          variant={"outline"}
                          size={"md"}
                          iconOnly
                          leftIcon={<LuTrash2 size={17} />}
                          className={classNames(
                            iconButtonClassName,
                            emailsData.length > 1
                              ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                              : "cursor-not-allowed text-[#C3C8D4]",
                          )}
                          disabled={emailsData.length <= 1}
                          aria-label={"Delete email"}
                          onClick={() => handleDeleteEmail(index)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {emailError && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {emailError}
                </div>
              )}

              <div className={"mt-4"}>
                <FieldLabel label={"Phone Number"} size={"sm"} />
                {renderPhoneRows(
                  phoneData,
                  handleAddPhone,
                  handleDeletePhone,
                  setPhoneData,
                  "Add phone number",
                )}
                {phoneError && (
                  <div className={"pt-2 text-xs font-bold text-error"}>
                    {phoneError}
                  </div>
                )}
              </div>
            </div>

            <SectionTitle size={"sm"}>Address</SectionTitle>
            <div className={"grid gap-4"}>
              {addressesData.map((address, index) => (
                <div key={address.id} className={"flex items-start gap-2"}>
                  <div className={"flex h-10 w-6 shrink-0 items-center"}>
                    {index === 0 ? (
                      <Button
                        type={"button"}
                        variant={"primary"}
                        size={"xs"}
                        iconOnly
                        leftIcon={<LuPlus size={16} strokeWidth={3} />}
                        className={"shrink-0"}
                        aria-label={"Add address"}
                        onClick={handleAddAddress}
                      />
                    ) : (
                      <span className={"h-6 w-6"} />
                    )}
                  </div>
                  <div className={"grid min-w-0 flex-1 gap-3"}>
                    <div className={"flex items-center gap-2"}>
                      <Select
                        value={
                          ADDRESS_TYPES.find(
                            (item) => item.value === address.addressTypeEnumId,
                          ) || ADDRESS_TYPES[0]
                        }
                        options={ADDRESS_TYPES}
                        placeholder={"Select address type"}
                        size={"md"}
                        dropMenuVariant={"portal"}
                        styleConfig={{
                          fullWidth: true,
                          menuWidth: 240,
                          controlClassName: "min-h-0 ",
                        }}
                        onChange={(value) => {
                          const option = getSingleSelectOption(value);

                          if (!option) {
                            return;
                          }

                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      addressTypeEnumId: option.value,
                                    }
                                  : currentAddress,
                            ),
                          );
                        }}
                      />
                      <Button
                        type={"button"}
                        variant={"outline"}
                        size={"md"}
                        iconOnly
                        leftIcon={<LuTrash2 size={17} />}
                        className={classNames(
                          iconButtonClassName,
                          addressesData.length > 1
                            ? "text-[#D0473F] hover:border-[#E7E9F0] hover:bg-[#FDF1F1]"
                            : "cursor-not-allowed text-[#C3C8D4]",
                        )}
                        disabled={addressesData.length <= 1}
                        aria-label={"Delete address"}
                        onClick={() => handleDeleteAddress(index)}
                      />
                    </div>
                    <Input
                      placeholder={"Street address"}
                      value={address.line1}
                      onChange={(event) =>
                        setAddressesData((currentAddresses) =>
                          currentAddresses.map((currentAddress, addressIndex) =>
                            addressIndex === index
                              ? {
                                  ...currentAddress,
                                  line1: event.target.value,
                                }
                              : currentAddress,
                          ),
                        )
                      }
                    />
                    <div className={"grid grid-cols-2 gap-2"}>
                      <Input
                        placeholder={region.cityLabel}
                        value={address.city}
                        onChange={(event) =>
                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      city: event.target.value,
                                    }
                                  : currentAddress,
                            ),
                          )
                        }
                      />
                      <Input
                        placeholder={region.regionLabel}
                        value={address.region}
                        onChange={(event) =>
                          setAddressesData((currentAddresses) =>
                            currentAddresses.map(
                              (currentAddress, addressIndex) =>
                                addressIndex === index
                                  ? {
                                      ...currentAddress,
                                      region: event.target.value,
                                    }
                                  : currentAddress,
                            ),
                          )
                        }
                      />
                    </div>
                    <Input
                      placeholder={region.postalLabel}
                      value={address.postalCode}
                      onChange={(event) =>
                        setAddressesData((currentAddresses) =>
                          currentAddresses.map((currentAddress, addressIndex) =>
                            addressIndex === index
                              ? {
                                  ...currentAddress,
                                  postalCode: event.target.value,
                                }
                              : currentAddress,
                          ),
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            <Divider />

            <SectionTitle size={"sm"}>Registration & Tax</SectionTitle>
            <div className={"mb-4"}>
              <FieldLabel label={"Incorporation Date"} size={"sm"} />
              <Input
                placeholder={"YYYY/MM/DD"}
                inputMode={"numeric"}
                maxLength={10}
                error={!!errors.incorporationDate}
                // Enter keeps the typed date in the box; Save saves (QA batch 8).
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                {...register("incorporationDate", {
                  validate: validateDateInput,
                  onChange: (event) => {
                    setValue(
                      "incorporationDate",
                      formatDateMask(event.target.value),
                      { shouldValidate: true },
                    );
                  },
                })}
              />
              {errors.incorporationDate && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.incorporationDate.message}
                </div>
              )}
            </div>

            <div className={"mb-4 grid grid-cols-2 gap-4"}>
              <div>
                <FieldLabel label={"Year End Month"} size={"sm"} />
                <Controller
                  control={control}
                  name={"yearEndMonth"}
                  render={({ field }) => (
                    <Select
                      value={getSelectValue(field.value)}
                      placeholder={"Select month"}
                      options={monthsOptions.map(mapSelectOption)}
                      size={"md"}
                      dropMenuVariant={"portal"}
                      onChange={(value) => {
                        const option = getSingleSelectOption(value);

                        if (option) {
                          field.onChange(option);
                        }
                      }}
                      styleConfig={{ fullWidth: true }}
                    />
                  )}
                />
              </div>
              <div>
                <FieldLabel label={"Year End Day"} size={"sm"} />
                <Controller
                  control={control}
                  name={"yearEndDay"}
                  render={({ field }) => (
                    <Select
                      value={getSelectValue(field.value)}
                      placeholder={"Select day"}
                      options={
                        getDaysOptions(
                          watch("yearEndMonth")?.value as MonthsEnum,
                        ).map(mapSelectOption) || []
                      }
                      size={"md"}
                      dropMenuVariant={"portal"}
                      loading={isLoadingOrganizationTypes}
                      onChange={(value) => {
                        const option = getSingleSelectOption(value);

                        if (option) {
                          field.onChange(option);
                        }
                      }}
                      styleConfig={{ fullWidth: true }}
                    />
                  )}
                />
              </div>
            </div>

            <div className={"mb-4"}>
              <FieldLabel label={region.businessId.label} size={"sm"} />
              <Input
                placeholder={region.businessId.placeholder}
                inputMode={region.code === "GB" ? "text" : "numeric"}
                maxLength={businessIdMaxLength(region.code)}
                error={!!errors.businessNumber}
                {...register("businessNumber", {
                  validate: (value) =>
                    businessIdProblem(
                      region.code,
                      value,
                      organization?.businessNumber || "",
                    ) || true,
                  onChange: (event) => {
                    setValue(
                      "businessNumber",
                      formatBusinessId(region.code, event.target.value),
                    );
                  },
                })}
              />
              {errors.businessNumber && (
                <div className={"pt-2 text-xs font-bold text-error"}>
                  {errors.businessNumber.message}
                </div>
              )}
            </div>

            <div className={"mb-4 grid grid-cols-[1fr_160px] gap-4"}>
              <div>
                <FieldLabel label={region.salesTaxLabel} size={"sm"} />
                <Input
                  placeholder={region.salesTaxLabel}
                  {...register("gstHstNumber")}
                />
              </div>
              <div>
                <FieldLabel label={region.salesTaxPeriodLabel} size={"sm"} />
                <Controller
                  control={control}
                  name={"gstHstReportingPeroid"}
                  render={({ field }) => (
                    <Select
                      value={getSelectValue(field.value)}
                      placeholder={"Select period"}
                      options={reportingPeriodGst}
                      size={"md"}
                      dropMenuVariant={"portal"}
                      onChange={(value) => {
                        const option = getSingleSelectOption(value);

                        if (option) {
                          field.onChange(option);
                        }
                      }}
                      styleConfig={{ fullWidth: true }}
                    />
                  )}
                />
              </div>
            </div>

            {/* PST is a Canadian provincial tax: hidden for firms in other countries. */}
            {region.code === "CA" && (
              <div className={"mb-4 grid grid-cols-[1fr_160px] gap-4"}>
                <div>
                  <FieldLabel label={"PST Number"} size={"sm"} />
                  <Input placeholder={"PST number"} {...register("pstNumber")} />
                </div>
                <div>
                  <FieldLabel label={"Reporting Period"} size={"sm"} />
                  <Controller
                    control={control}
                    name={"pstReportingPeroid"}
                    render={({ field }) => (
                      <Select
                        value={getSelectValue(field.value)}
                        placeholder={"Select period"}
                        options={reportingPeriodPst}
                        size={"md"}
                        dropMenuVariant={"portal"}
                        onChange={(value) => {
                          const option = getSingleSelectOption(value);

                          if (option) {
                            field.onChange(option);
                          }
                        }}
                        styleConfig={{ fullWidth: true }}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            <div className={"mb-4"}>
              <FieldLabel label={"Organization Type"} size={"sm"} />
              <Controller
                control={control}
                name={"organizationTypeId"}
                render={({ field }) => (
                  <Select
                    value={getSelectValue(field.value)}
                    placeholder={"Select organization type"}
                    options={
                      organizationTypeOptions.length || !field.value?.value
                        ? organizationTypeOptions
                        : [field.value]
                    }
                    size={"md"}
                    dropMenuVariant={"portal"}
                    loading={isLoadingOrganizationTypes}
                    onChange={(value) => {
                      const option = getSingleSelectOption(value);

                      if (option) {
                        field.onChange(option);
                      }
                    }}
                    styleConfig={{ fullWidth: true }}
                  />
                )}
              />
            </div>

            <Divider />

            <CustomFieldsEditSection
              entityType={"organization"}
              values={customFields.values}
              onChange={customFields.setValue}
              showErrors={customFields.showErrors}
            />
            {customFieldError && (
              <div className={"mt-2 text-sm font-semibold text-error"}>
                {customFieldError}
              </div>
            )}

            <Divider />

            <Controller
              control={control}
              name={"contactType"}
              render={({ field }) => (
                <div className={"mb-5"}>
                  <SectionTitle size={"sm"}>Contact Type</SectionTitle>
                  <div className={"flex flex-wrap gap-2"}>
                    {contactTypeButtons.map((contactType) => (
                      <Button
                        key={contactType.value}
                        type={"button"}
                        variant={"ghost"}
                        size={"sm"}
                        autoWidth
                        className={classNames(
                          "h-auto border px-3 py-2 text-sm font-semibold",
                          contactType.isSelected
                            ? "border-[#6B4EFF] bg-[#F7F5FF] text-[#6B4EFF]"
                            : "border-[#DCE2EA] bg-white text-[#3A4256] hover:border-[#C5CCDA]",
                        )}
                        disabled={isLoadingContactTypes}
                        onClick={() => field.onChange(contactType)}>
                        {contactType.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            />

            <div>
              <SectionTitle size={"sm"}>Additional Info</SectionTitle>
              <Input
                as={"textarea"}
                placeholder={"Add any notes about this contact..."}
                rows={4}
                className={"resize-y"}
                {...register("additionalInfo")}
              />
            </div>
          </div>

          <div
            className={
              "flex shrink-0 justify-end gap-2 border-t border-[#EFF0F4] bg-white px-7 py-4"
            }>
            <Button
              type={"button"}
              variant={"outline"}
              size={"md"}
              width={100}
              disabled={isSubmitting}
              onClick={onClose}>
              Cancel
            </Button>
            <Button
              type={"submit"}
              variant={"primary"}
              size={"md"}
              width={100}
              loading={isLoadingEdit}
              disabled={
                isSubmitting || isLoadingOrganization || Boolean(emailError)
              }>
              Save
            </Button>
          </div>

          <ReactTooltip
            id={PRIMARY_EMAIL_TOOLTIP_ID}
            place={"bottom"}
            className={"!rounded-[8px] !p-2 !text-sm"}
          />
        </form>
      </div>
      {duplicateEmailDialog}
    </div>
  );
};
